import * as React from 'react'
import { useState } from 'react'
import I18n from '../../../libs/i18n'
import DateTimeInput from '../../shared/date_time_input'
import { format } from 'date-fns'

type PreparationDatesProps = {
  preparationStartDate?: string | null
  preparationEndDate?: string | null
  firstNotificationDate?: string | null
  secondNotificationDate?: string | null
}

const PreparationDates: React.FC<PreparationDatesProps> = ({
  preparationStartDate: initialPreparationStartDate = null,
  preparationEndDate: initialPreparationEndDate = null,
  firstNotificationDate: initialFirstNotificationDate = null,
  secondNotificationDate: initialSecondNotificationDate = null,
}) => {
  const parseDate = (dateString: string | null): Date | null => {
    if (!dateString) return null
    const [day, month, year] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const [preparationStartDate, setPreparationStartDate] = useState<Date | null>(parseDate(initialPreparationStartDate))
  const [preparationEndDate, setPreparationEndDate] = useState<Date | null>(parseDate(initialPreparationEndDate))
  const [firstNotificationDate, setFirstNotificationDate] = useState<Date | null>(parseDate(initialFirstNotificationDate))
  const [secondNotificationDate, setSecondNotificationDate] = useState<Date | null>(parseDate(initialSecondNotificationDate))
  const [showFirstNotification, setShowFirstNotification] = useState(!!initialFirstNotificationDate)
  const [showSecondNotification, setShowSecondNotification] = useState(!!initialSecondNotificationDate)

  const formatDateForServer = (date: Date | null): string => {
    if (!date) return ''
    return format(date, 'dd-MM-yyyy')
  }

  const handleDateChange = (field: string, value: Date | null) => {
    switch (field) {
      case 'preparation_start_date':
        setPreparationStartDate(value)
        if (!value) {
          setFirstNotificationDate(null)
          setShowFirstNotification(false)
        }
        break
      case 'preparation_end_date':
        setPreparationEndDate(value)
        if (!value) {
          setSecondNotificationDate(null)
          setShowSecondNotification(false)
        }
        break
      case 'first_notification_date':
        setFirstNotificationDate(value)
        break
      case 'second_notification_date':
        setSecondNotificationDate(value)
        break
    }
  }

  const handleCheckboxChange = (field: string, checked: boolean) => {
    if (field === 'firstNotification') {
      setShowFirstNotification(checked)
      if (!checked) {
        setFirstNotificationDate(null)
      }
    } else {
      setShowSecondNotification(checked)
      if (!checked) {
        setSecondNotificationDate(null)
      }
    }
  }

  return (
    <div className="preparation-dates">
      <div className="row mb-3">
        <div className="col-6">
          <div className="form-group">
            <label className="label">
              {I18n.t('activerecord.attributes.campaigns/campaign.preparation_start_date')}
            </label>
            <div className="default-form-input-wrapper date-picker">
              <DateTimeInput
                value={preparationStartDate}
                onChange={(value) => handleDateChange('preparation_start_date', value)}
                name="campaign[preparation_start_date]"
                as="date"
                showMonthDropdown
                showYearDropdown
              />
            </div>
            {preparationStartDate && (
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="firstNotificationCheckbox"
                  checked={showFirstNotification}
                  onChange={(e) => handleCheckboxChange('firstNotification', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="firstNotificationCheckbox">
                  {I18n.t('campaigns.form.enable_first_notification')}
                </label>
              </div>
            )}
            {showFirstNotification && (
              <div className="form-group mt-3">
                <label className="label">
                  {I18n.t('activerecord.attributes.campaigns/campaign.first_notification_date')}
                </label>
                <div className="default-form-input-wrapper date-picker">
                  <DateTimeInput
                    value={firstNotificationDate}
                    onChange={(value) => handleDateChange('first_notification_date', value)}
                    name="campaign[first_notification_date]"
                    required
                    as="date"
                    showMonthDropdown
                    showYearDropdown
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="col-6">
          <div className="form-group">
            <label className="label">
              {I18n.t('activerecord.attributes.campaigns/campaign.preparation_end_date')}
            </label>
            <div className="default-form-input-wrapper date-picker">
              <DateTimeInput
                value={preparationEndDate}
                onChange={(value) => handleDateChange('preparation_end_date', value)}
                name="campaign[preparation_end_date]"
                as="date"
                showMonthDropdown
                showYearDropdown
              />
            </div>
            {preparationEndDate && (
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="secondNotificationCheckbox"
                  checked={showSecondNotification}
                  onChange={(e) => handleCheckboxChange('secondNotification', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="secondNotificationCheckbox">
                  {I18n.t('campaigns.form.enable_second_notification')}
                </label>
              </div>
            )}
            {showSecondNotification && (
              <div className="form-group mt-3">
                <label className="label">
                  {I18n.t('activerecord.attributes.campaigns/campaign.second_notification_date')}
                </label>
                <div className="default-form-input-wrapper date-picker">
                  <DateTimeInput
                    value={secondNotificationDate}
                    onChange={(value) => handleDateChange('second_notification_date', value)}
                    name="campaign[second_notification_date]"
                    required
                    as="date"
                    showMonthDropdown
                    showYearDropdown
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <input type="hidden" name="campaign[preparation_start_date]" value={formatDateForServer(preparationStartDate)} />
      <input type="hidden" name="campaign[preparation_end_date]" value={formatDateForServer(preparationEndDate)} />
      <input type="hidden" name="campaign[first_notification_date]" value={formatDateForServer(firstNotificationDate)} />
      <input type="hidden" name="campaign[second_notification_date]" value={formatDateForServer(secondNotificationDate)} />
    </div>
  )
}

export default PreparationDates 